import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from '@/lib/utils';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  /** Texto, listas, links e imagens (upload via onImageUpload). */
  richContent?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
}

// Emojis comuns organizados por categoria
const emojiCategories = {
  'Faces': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙'],
  'Gestos': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍'],
  'Objetos': ['🏠', '🚗', '✈️', '🚢', '🚲', '🏖️', '🌴', '🌊', '☀️', '🌙', '⭐', '🎉', '🎊', '🎈', '🎁', '🎂', '🍕', '🍔', '🍟', '🍦'],
  'Símbolos': ['✅', '❌', '⚠️', 'ℹ️', '💡', '🔥', '💯', '⭐', '🌟', '✨', '💫', '💥', '💢', '💤', '💨', '💦', '💧', '🌊', '🌈', '☀️'],
  'Natureza': ['🌳', '🌲', '🌴', '🌵', '🌱', '🌿', '🍀', '🍃', '🍂', '🍁', '🌾', '🌷', '🌹', '🌺', '🌻', '🌼', '🌸', '💐', '🌰', '🌍'],
};

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Digite seu texto aqui...",
  className,
  minHeight = '200px',
  richContent = false,
  onImageUpload,
}: RichTextEditorProps) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);

  // Handler para inserir emoji
  const insertEmoji = (emoji: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      quill.insertText(index, emoji);
      quill.setSelection(index + emoji.length);
    }
    setShowEmojiPicker(false);
  };

  // Adicionar botão de emoji ao toolbar após o ReactQuill ser montado
  useEffect(() => {
    const addEmojiButton = () => {
      if (quillRef.current && toolbarRef.current) {
        const toolbar = toolbarRef.current.querySelector('.ql-toolbar');
        if (toolbar && !toolbar.querySelector('.ql-emoji-button')) {
          const emojiButton = document.createElement('button');
          emojiButton.type = 'button';
          emojiButton.className = 'ql-emoji-button';
          emojiButton.innerHTML = '😊';
          emojiButton.title = 'Inserir emoji';
          emojiButton.setAttribute('aria-label', 'Inserir emoji');
          emojiButtonRef.current = emojiButton;
          emojiButton.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowEmojiPicker((prev) => !prev);
          };
          
          // Adicionar separador e botão ao final do toolbar
          const separator = document.createElement('span');
          separator.className = 'ql-formats';
          separator.appendChild(emojiButton);
          toolbar.appendChild(separator);
        }
      }
    };

    // Aguardar um pouco para garantir que o ReactQuill renderizou o toolbar
    const timeout = setTimeout(addEmojiButton, 100);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!richContent || !onImageUpload) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const toolbar = quill.getModule('toolbar') as {
      addHandler: (name: string, handler: () => void) => void;
    };
    if (!toolbar?.addHandler) return;

    toolbar.addHandler('image', () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const url = await onImageUpload(file);
          const range = quill.getSelection(true);
          const index = range?.index ?? quill.getLength();
          quill.insertEmbed(index, 'image', url);
          quill.setSelection(index + 1);
        } catch {
          /* upload tratado pelo chamador */
        }
      };
      input.click();
    });
  }, [richContent, onImageUpload]);

  const toolbarContainer = useMemo(() => {
    const base: (string | Record<string, unknown>)[][] = [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: ['', 'center'] }],
    ];
    if (richContent) {
      base.push(['link', 'image']);
    }
    return base;
  }, [richContent]);

  const modules = useMemo(
    () => ({
      toolbar: { container: toolbarContainer },
    }),
    [toolbarContainer],
  );

  const formats = useMemo(() => {
    const base = [
      'bold',
      'italic',
      'underline',
      'list',
      'bullet',
      'align',
    ];
    if (richContent) {
      return [...base, 'link', 'image'];
    }
    return base;
  }, [richContent]);

  return (
    <div className={cn("border rounded-md", className)} ref={toolbarRef}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        style={{ minHeight }}
      />
      {/* Popover de emojis */}
      <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
        <PopoverTrigger asChild>
          <div 
            className="absolute w-0 h-0 pointer-events-none"
            style={{ 
              top: emojiButtonRef.current ? `${emojiButtonRef.current.getBoundingClientRect().top - toolbarRef.current?.getBoundingClientRect().top || 0}px` : '0px',
              right: '0px'
            }}
          />
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-4 max-h-96 overflow-y-auto" 
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <div className="space-y-4">
            {Object.entries(emojiCategories).map(([category, emojis]) => (
              <div key={category}>
                <h4 className="text-sm font-semibold mb-2 text-foreground">{category}</h4>
                <div className="grid grid-cols-10 gap-1">
                  {emojis.map((emoji, index) => (
                    <button
                      key={`${category}-${index}`}
                      type="button"
                      onClick={() => insertEmoji(emoji)}
                      className="text-xl hover:bg-accent rounded p-1 transition-colors cursor-pointer"
                      title={emoji}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <style>{`
        .ql-container {
          font-family: inherit;
          font-size: inherit;
          border-bottom: none;
          border-left: none;
          border-right: none;
          border-top: none;
          background: hsl(var(--background));
        }
        .ql-editor {
          min-height: ${minHeight};
          max-height: ${minHeight};
          overflow-y: auto;
          color: hsl(var(--foreground));
        }
        .ql-toolbar {
          border-top: none;
          border-left: none;
          border-right: none;
          border-bottom: 1px solid hsl(var(--border));
          background: hsl(var(--background));
        }
        .ql-toolbar .ql-stroke {
          stroke: hsl(var(--foreground));
        }
        .ql-toolbar .ql-fill {
          fill: hsl(var(--foreground));
        }
        .ql-toolbar button:hover,
        .ql-toolbar button:focus {
          background: hsl(var(--accent));
        }
        .ql-toolbar button.ql-active {
          background: hsl(var(--accent));
        }
        .ql-editor.ql-blank::before {
          color: hsl(var(--muted-foreground));
          font-style: normal;
        }
        .ql-snow .ql-picker {
          color: hsl(var(--foreground));
        }
        .ql-snow .ql-picker-options {
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
        }
        .ql-snow .ql-picker-item:hover {
          background: hsl(var(--accent));
        }
        .ql-toolbar .ql-emoji-button {
          width: 28px;
          height: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: none;
          background: transparent;
          padding: 2px;
          font-size: 18px;
          line-height: 1;
        }
        .ql-toolbar .ql-emoji-button:hover {
          background: hsl(var(--accent));
          border-radius: 2px;
        }
        .ql-toolbar .ql-emoji-button:active {
          background: hsl(var(--accent));
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;

